'use client'

import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode, type Ref } from 'react'

import { FormattedPrice } from '@src/blocks/currency'
import FilledStar from '@src/components/product-card/icons/filledStar.inline.svg'
import { ProductTags } from '@src/components/product-card/product-image-section/product-tags/product-tags'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { Icon } from '@src/quince-components/src'
import { Image } from '@src/quince-components/src/atoms/image/Image'
import Link from '@src/quince-components/src/atoms/link/Link'
import type { TrackConfig } from '@src/quince-components/src/hocs/withTracking'
import { SnapScroll } from '@src/quince-components/src/molecules/snap-scroll'
import { getIsNativeViewClient } from '@src/quince-site-common/src/helpers/getIsNativeViewClient'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useIsVisible from '@src/quince-site-common/src/hooks/useIsVisible'
import { TAG_LOCATION, MAX_NUMBER_OF_COLOR_OPTIONS } from '@src/quince-site-common/src/utils/constants'
import { getFormattedAverageRating } from '@src/quince-site-common/src/utils/getFormattedAverageRating'
import type { SvdNativeCardVariant, SvdNativeProduct } from '@src/types/svd-product-card.types'

import { fireSvdProductClick, fireSvdVariantSelect, useSvdProductCardImpression } from './svd-product-card.analytics'
import {
  getInitialSvdVariant,
  getSvdApplicableTags,
  getSvdSocialProofLabels,
  getSvdSwatchVariants,
  shouldRenderSvdSwatches,
  SVD_SWATCH_DISPLAY_TYPE,
} from './svd-product-card.helpers'
import styles from './svd-product-card.module.scss'
import { SvdCardAddToWishlist } from './wishlist/svd-card-add-to-wishlist'

export interface SvdProductCardProps {
  product: SvdNativeProduct
  className?: string
  /** Rendered tile width (px) — forwarded to the image loader for right-sized requests. */
  maxWidth?: number
  /**
   * Opt-in mobile image carousel (mirrors the legacy card's `showMScroll` / `enableImageCarousel`).
   * Off by default — legacy passes `showMScroll={false}` in every product-list/landing section, so
   * the SVD card matches prod (single image on mobile) unless a section explicitly turns it on.
   */
  enableImageCarousel?: boolean
  /**
   * Analytics — all optional; sections that track pass these. Click tracking (`PRODUCT_CLICK`) needs
   * `productClickTrackConfig`; impression (`BULK_PRODUCT_CARD_VIEW`) needs `productViewTracker` (the
   * section must also `initializeBatchedAnalyticsEvent` for it). `productSearchObjectId` /
   * `productPosition` / `searchAlgoliaQueryId` drive the Algolia click on search surfaces only.
   */
  productClickTrackConfig?: TrackConfig
  productViewTracker?: string
  onClickCb?: () => void
  productSearchObjectId?: string
  productPosition?: number
  searchAlgoliaQueryId?: string
  /**
   * LCP — above-fold tiles opt in so the first image loads `eager` + `fetchPriority="high"`, and
   * `imageRef` exposes that element for LCP measurement. Off by default (lazy), matching prod.
   */
  eagerLoadImage?: boolean
  imageRef?: Ref<HTMLImageElement>
  /**
   * Controlled variant — a section can drive the selected variant (`selectedVariantId`) and observe
   * changes (`onVariantChange`). Pseudo-controlled: the card keeps its own state but re-syncs to the
   * id. Used by wishlist / post-order / reco surfaces.
   */
  selectedVariantId?: number
  onVariantChange?: (variant: SvdNativeCardVariant) => void
  /**
   * Custom footer + visibility flags (parity with the legacy card). `customFooter`, when set,
   * replaces the default brand/title/price/rating footer. The `hide*` flags gate individual pieces.
   */
  customFooter?: ReactNode
  hideTitle?: boolean
  hideReviews?: boolean
  hideTags?: boolean
  hideSwatches?: boolean
  /**
   * Wishlist heart. Rendered when the product allows it (`flags.wishlistEnabled`, non-bundle) and
   * `hideWishlist` is not set. Guests persist to localStorage only. `wishlistTrackingConfig` is
   * optional section-provided tracking context (search/position etc.).
   */
  hideWishlist?: boolean
  wishlistTrackingConfig?: Record<string, unknown>
}

/**
 * SVD product card — renders directly off the native `SvdNativeProduct` (generic anchor swatches,
 * nullable `displayConfig`, per-variant `spid`/`url`). Image (hover-swap on desktop / opt-in mobile
 * carousel), title, brand, price (+ isRange), rating, swatch row + selection, tags, social proof,
 * click/impression/variant-select analytics, LCP hints, controlled variant, custom footer + hide
 * flags, and a native-webview navigation bridge. Reached via the section-level `shouldUseSvdCard()`
 * branch; the legacy card is untouched.
 */
export function SvdProductCard({
  product,
  className,
  maxWidth,
  enableImageCarousel = false,
  productClickTrackConfig,
  productViewTracker,
  onClickCb,
  productSearchObjectId,
  productPosition,
  searchAlgoliaQueryId,
  eagerLoadImage = false,
  imageRef,
  selectedVariantId,
  onVariantChange,
  customFooter,
  hideTitle = false,
  hideReviews = false,
  hideTags = false,
  hideSwatches = false,
  hideWishlist = false,
  wishlistTrackingConfig,
}: SvdProductCardProps) {
  const { t } = useTranslation('common')
  const { cardVariants, title, thirdPartyDetails, reviews } = product

  const [selectedVariant, setSelectedVariant] = useState<SvdNativeCardVariant | undefined>(() =>
    getInitialSvdVariant(cardVariants, selectedVariantId),
  )

  // Impression tracking: fire `BULK_PRODUCT_CARD_VIEW` once the card scrolls into view (0.85 vis,
  // matching the legacy card). The section owns the batched-analytics init; this only produces.
  const wrapperRef = useRef<HTMLElement>(null)
  const { isVisible } = useIsVisible(wrapperRef, 0.85)

  // Re-derive the selected variant when the data or the controlled id changes (pseudo-controlled,
  // mirroring the legacy card — the card keeps its own copy but a parent can drive it).
  useEffect(() => {
    setSelectedVariant(getInitialSvdVariant(cardVariants, selectedVariantId))
  }, [cardVariants, selectedVariantId])

  const brandName = thirdPartyDetails.brandDisplayName ?? thirdPartyDetails.brand
  // Parity with the legacy card: hovering swaps the image *source* to the variant's second image
  // (pointer devices only — touch never fires mouseenter). No fade/transition, matching legacy.
  const [isHovered, setIsHovered] = useState(false)
  const variantImages = selectedVariant?.images ?? []
  const hoverImage = variantImages[1]
  const displayImage = isHovered && hoverImage ? hoverImage : variantImages[0]
  const { deviceType } = useDeviceInfo()
  const showImageCarousel = enableImageCarousel && deviceType === DEVICE_TYPES.MOBILE && variantImages.length > 1
  const formattedRating = getFormattedAverageRating(reviews.averageRating)
  const showSwatches = !hideSwatches && shouldRenderSvdSwatches(product)
  // Cap the swatch row (parity with the legacy `optionLimit`); the rest collapse into a "+N" link.
  const { visible: swatchVariants, overflowCount } = getSvdSwatchVariants(cardVariants, MAX_NUMBER_OF_COLOR_OPTIONS)
  const selectedAnchorLabel = selectedVariant?.displayConfig?.displayName
  const targetUrl = selectedVariant?.url ?? product.slug

  // Backend-driven tags (EDD / white-glove / new / sale, etc.) already arrive on the wire per
  // variant; an active promo's pills replace them unless the variant is sold out. Rendered via the
  // shared color-free ProductTags leaf.
  const applicableTags = getSvdApplicableTags(selectedVariant)
  // Social proof: stock urgency + recent-sales copy, backend-provided (empty for every product on
  // the wire today — design-pending).
  const socialProofLabels = getSvdSocialProofLabels(selectedVariant?.socialProofData)

  useSvdProductCardImpression({ productViewTracker, wrapperRef, isVisible, product, selectedVariant, applicableTags })

  // Swatch pick: fire variant-select analytics, update local state, notify a controlling parent.
  const handleSelectVariant = (variant: SvdNativeCardVariant) => {
    fireSvdVariantSelect({
      product,
      previousVariant: selectedVariant,
      nextVariant: variant,
      trackConfig: productClickTrackConfig,
    })
    setSelectedVariant(variant)
    onVariantChange?.(variant)
  }

  // Card click: fire click analytics + parent callback, then — inside the native (hybrid app)
  // webview — bridge navigation via postMessage instead of a browser nav (mirrors the legacy card).
  // postMessage first; only preventDefault on success, so a failure falls back to the browser nav.
  const handleCardClick = (event: ReactMouseEvent) => {
    fireSvdProductClick({
      product,
      selectedVariant,
      applicableTags,
      trackConfig: productClickTrackConfig,
      onClickCb,
      productSearchObjectId,
      productPosition,
      searchAlgoliaQueryId,
    })
    if (getIsNativeViewClient()) {
      try {
        window.ReactNativeWebView?.postMessage(
          JSON.stringify({
            action: 'NAVIGATION',
            url: window.location.href,
            newURL: targetUrl.split('?')[0],
            nextURL: targetUrl,
          }),
        )
        event.preventDefault()
      } catch (error) {
        // eslint-disable-next-line no-console -- surface bridge failures; Link nav then proceeds
        console.error('SVD card: native webview postMessage failed', error)
      }
    }
  }

  const defaultFooter = (
    <>
      {brandName && <div className={styles.brand}>{brandName}</div>}
      {!hideTitle && (
        <h2 className={styles.title} data-testid={TEST_LOCATORS.PRODUCT_CARD_TITLE}>
          {title}
        </h2>
      )}
      {/* a11y: announce the selected anchor (e.g. colour) to screen readers, generic over anchor name */}
      {selectedAnchorLabel && <span className={styles.srOnly}>{selectedAnchorLabel}</span>}
      <div className={styles.priceRow} data-testid={TEST_LOCATORS.PRODUCT_CARD_PRICE}>
        {selectedVariant?.price.isRange && <span className={styles.fromPrefix}>{t('from')}</span>}
        <FormattedPrice amount={selectedVariant?.price.salePrice} />
      </div>
      {!hideReviews && formattedRating && (
        <div className={styles.rating}>
          <Icon className={styles.ratingIcon} iconSvg={FilledStar} />
          <span>{formattedRating}</span>
          {reviews.reviewCount > 0 && <span className={styles.ratingCount}>{`(${reviews.reviewCount})`}</span>}
        </div>
      )}
      {socialProofLabels.length > 0 && (
        <div className={styles.socialProof}>
          {socialProofLabels.map(label => (
            <span key={label} className={styles.socialProofItem}>
              {label}
            </span>
          ))}
        </div>
      )}
    </>
  )

  return (
    <article
      ref={wrapperRef}
      className={classnames(styles.svdProductCard, className)}
      data-testid={TEST_LOCATORS.PRODUCT_CARD}
    >
      {/* Wishlist heart — sibling of the link (never nested in the anchor); absolutely positioned
          over the image. Rendered only when the product allows it + a section hasn't hidden it. */}
      {!hideWishlist && product.flags.wishlistEnabled && product.type !== 'Bundle' && (
        <SvdCardAddToWishlist
          product={product}
          selectedVariant={selectedVariant}
          wishlistTrackingConfig={wishlistTrackingConfig}
        />
      )}
      <Link href={targetUrl} className={styles.link} onClick={handleCardClick}>
        <div
          className={styles.imageSection}
          onMouseEnter={() => {
            setIsHovered(true)
          }}
          onMouseLeave={() => {
            setIsHovered(false)
          }}
        >
          <div className={styles.imageContainer}>
            {showImageCarousel ? (
              // Remount (key) on variant change so the carousel resets to the first image.
              <SnapScroll
                key={selectedVariant?.variantId}
                id={`svd-card-images-${product.productId}-${selectedVariant?.variantId ?? 0}`}
                spaceBetween={0}
              >
                {variantImages.map((image, imageIndex) => {
                  const isFirstEager = imageIndex === 0 && eagerLoadImage
                  return (
                    <Image
                      key={image.id}
                      ref={imageIndex === 0 ? imageRef : undefined}
                      url={image.url}
                      alt={image.alt || title}
                      title={image.alt || title}
                      id={image.id}
                      height={image.height}
                      width={image.width}
                      maxWidth={maxWidth}
                      loading={isFirstEager ? 'eager' : 'lazy'}
                      fetchPriority={isFirstEager ? 'high' : undefined}
                      className={styles.image}
                    />
                  )
                })}
              </SnapScroll>
            ) : (
              displayImage && (
                <Image
                  ref={imageRef}
                  url={displayImage.url}
                  alt={displayImage.alt || title}
                  title={displayImage.alt || title}
                  id={displayImage.id}
                  height={displayImage.height}
                  width={displayImage.width}
                  maxWidth={maxWidth}
                  loading={eagerLoadImage ? 'eager' : 'lazy'}
                  fetchPriority={eagerLoadImage ? 'high' : undefined}
                  className={styles.image}
                />
              )
            )}
          </div>
          {!hideTags && applicableTags.length > 0 && (
            <ProductTags applicableTags={applicableTags} tagLocation={TAG_LOCATION.PRODUCT_CARD} />
          )}
        </div>
      </Link>

      {showSwatches && (
        <div className={styles.swatchRow} data-testid={TEST_LOCATORS.PRODUCT_CARD_SELECTED_COLOR}>
          {swatchVariants.map(variant => {
            const config = variant.displayConfig
            if (!config) return null
            const isSelected = variant.variantId === selectedVariant?.variantId
            // Render per swatch mode, driven by the contract's `displayType`: TEXT → a text chip,
            // IMAGE → an image swatch, HEX_COLOR → a colour chip via `backgroundColor`.
            const isTextSwatch = config.displayType === SVD_SWATCH_DISPLAY_TYPE.TEXT
            const isImageSwatch = config.displayType === SVD_SWATCH_DISPLAY_TYPE.IMAGE
            return (
              <button
                key={variant.variantId}
                type="button"
                aria-label={config.displayName}
                aria-pressed={isSelected}
                className={classnames(styles.swatch, {
                  [styles.swatchText ?? '']: isTextSwatch,
                  [styles.swatchSelected ?? '']: isSelected,
                  [styles.swatchBordered ?? '']: Boolean(config.addBorder),
                })}
                style={!isTextSwatch && config.hexCode ? { backgroundColor: config.hexCode } : undefined}
                onClick={() => {
                  handleSelectVariant(variant)
                }}
              >
                {isTextSwatch && (config.displayName || config.value)}
                {isImageSwatch && config.imgURL && <img src={config.imgURL} alt={config.displayName} />}
              </button>
            )
          })}
          {overflowCount > 0 && (
            <Link href={targetUrl} className={styles.swatchOverflow}>
              {`+${overflowCount}`}
            </Link>
          )}
        </div>
      )}

      <Link href={targetUrl} className={styles.details} onClick={handleCardClick}>
        {customFooter ?? defaultFooter}
      </Link>
    </article>
  )
}
